import { getAppScheme } from './environments';

/**
 * Single entry point for "return to the Kizu mobile app" from the web UI.
 *
 * When the page is rendered inside the Kizu mobile WebView, we post a
 * message the native side handles (close the modal, optionally navigate).
 * Firing a kizu://… URL from inside the WebView produces an "Open in Kizu?"
 * system prompt on iOS because the app is being asked to open itself.
 *
 * When the page is rendered in a regular browser, we fall back to the
 * custom URL scheme as before.
 *
 * Every deep-link attempt in Kizu-Web should go through this helper so
 * the WebView guard is applied uniformly.
 */

interface ReturnToAppOptions {
  /**
   * Optional deep-link path to open (e.g. 'auth-session/abc', 'album-invite/xyz').
   * If omitted, in a browser we do nothing visible; in a WebView we just
   * close the modal.
   */
  path?: string;
  /**
   * Optional payload forwarded to the native side via postMessage.
   * The native `onMessage` handler in the WebView decides what to do with it.
   */
  payload?: Record<string, unknown>;
  /**
   * Message type to post to the native side. Defaults to 'KIZU_CLOSE',
   * which the mobile app handles as "close this WebView modal".
   */
  messageType?: string;
}

export function isInKizuWebView(): boolean {
  return typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;
}

export function returnToApp(options: ReturnToAppOptions = {}): void {
  const { path, payload, messageType = 'KIZU_CLOSE' } = options;

  if (isInKizuWebView()) {
    try {
      (window as any).ReactNativeWebView.postMessage(
        JSON.stringify({ type: messageType, path: path ?? null, ...(payload ?? {}) }),
      );
    } catch (err) {
      // If postMessage fails for any reason, fall through silently — the
      // alternative is firing a kizu:// URL that triggers the iOS prompt,
      // which is strictly worse than a no-op.
      console.error('[returnToApp] postMessage failed:', err);
    }
    return;
  }

  // Regular browser — fall back to the custom URL scheme. Safari / Chrome
  // will either open the app or show a can't-open prompt.
  if (path) {
    window.location.href = `${getAppScheme()}://${path}`;
  }
}
